import type { Action } from 'components/Layout/PaymentViewLayout';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useUserInfo } from 'hooks/useUserInfo';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { match } from 'ts-pattern';

import { LoadingText } from '@fragment/ui/src/components/LoadingText';

import { LoadingTextSuspense } from '../../LoadingTextSuspense/LoadingTextSuspense';
import { ApiClientsSettingsForm } from '../SchemasV2/WorkspaceSettings/ApiClientsSettingsForm';
import { BillingForm } from '../SchemasV2/WorkspaceSettings/BillingForm';
import { DataExportsSettingsForm } from '../SchemasV2/WorkspaceSettings/DataExportsSettingsForm';
import { MembersList } from '../SchemasV2/WorkspaceSettings/MembersList';
import { Preferences } from '../SchemasV2/WorkspaceSettings/Preferences';
import { Profile } from '../SchemasV2/WorkspaceSettings/Profile';
import { PaymentsGeneralSettingsForm } from './PaymentsGeneralSettingsForm';

type SettingsTab =
  | 'workspace'
  | 'api-clients'
  | 'members'
  | 'data-exports'
  | 'billing'
  | 'profile'
  | 'preferences';

type PaymentsSettingsPageProps = {
  tab?: string;
};

export const PaymentsSettingsPage = ({
  tab = 'workspace',
}: PaymentsSettingsPageProps) => {
  const { refreshUserState } = useUserInfo();
  const activeTab = (tab || 'workspace') as SettingsTab;
  const [headerAction, setHeaderAction] = useState<Action | undefined>(
    undefined
  );

  // Clear action when switching tabs
  useEffect(() => {
    setHeaderAction(undefined);
  }, [activeTab]);

  const handleAction = useCallback(
    (action: { title: string; onClick: () => void } | undefined) => {
      setHeaderAction(
        action
          ? {
              title: action.title,
              onClick: action.onClick,
              'data-testid': 'settings-header-action',
            }
          : undefined
      );
    },
    []
  );

  const title = useMemo(
    () =>
      match(activeTab)
        .with('workspace', () => 'General')
        .with('api-clients', () => 'API Clients')
        .with('members', () => 'Members')
        .with('data-exports', () => 'Data Exports')
        .with('billing', () => 'Billing')
        .with('profile', () => 'Profile')
        .with('preferences', () => 'Preferences')
        .otherwise(() => 'Settings'),
    [activeTab]
  );

  const content = useMemo(
    () =>
      match(activeTab)
        .with('workspace', () => (
          <LoadingTextSuspense text="Loading">
            <PaymentsGeneralSettingsForm onWorkspaceUpdate={refreshUserState} />
          </LoadingTextSuspense>
        ))
        .with('api-clients', () => (
          <ApiClientsSettingsForm hideHeader onAction={handleAction} />
        ))
        .with('members', () => (
          <MembersList hideHeader onAction={handleAction} />
        ))
        .with('data-exports', () => <DataExportsSettingsForm hideHeader />)
        .with('billing', () => <BillingForm hideHeader />)
        .with('profile', () => <Profile hideHeader />)
        .with('preferences', () => <Preferences hideHeader />)
        .otherwise(() => (
          <LoadingTextSuspense text="Loading">
            <PaymentsGeneralSettingsForm onWorkspaceUpdate={refreshUserState} />
          </LoadingTextSuspense>
        )),
    [activeTab, refreshUserState, handleAction]
  );

  return (
    <PaymentViewLayout
      title={title}
      primaryAction={headerAction}
      data-testid="payments-settings-page"
    >
      <div className="flex flex-col w-full px-f1 pt-f1 text-xmd">
        <Suspense fallback={<LoadingText text="Loading" />}>{content}</Suspense>
      </div>
    </PaymentViewLayout>
  );
};
